import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Zap, ShieldCheck, Sparkles, Clock, Users, Trophy, Gift, Repeat, ShoppingCart, Store, Wrench } from "lucide-react";
import { logger } from "@/lib/logger";
import api from "@/lib/api";

const REFERRAL_KEY = "rivendas_referrer";

function formatCount(n) {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

export default function Landing() {
  const { user, loading } = useAuth();
  const [referrer, setReferrer] = useState(null);
  const [stats, setStats] = useState(null);
  const [showcase, setShowcase] = useState({ completed_orders: [], active_users: [] });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(REFERRAL_KEY);
      if (raw) {
        const r = JSON.parse(raw);
        if (r?.user_id && Date.now() - (r.ts || 0) < 30 * 24 * 60 * 60 * 1000) {
          setReferrer(r);
        }
      }
    } catch (err) {
      logger.warn("landing: unable to read referral", err?.message);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    api.get("/public/stats")
      .then((r) => { if (alive) setStats(r.data); })
      .catch((err) => logger.warn("landing: stats", err?.message));
    api.get("/public/showcase")
      .then((r) => { if (alive) setShowcase(r.data || { completed_orders: [], active_users: [] }); })
      .catch((err) => logger.warn("landing: showcase", err?.message));
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <span className="inline-block w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-heading font-black text-lg">R</span>
            </span>
            <span className="font-heading font-black text-lg">
              <span className="text-primary">Ri</span>vendas
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="landing-login">Entrar</Button></Link>
            <Link to="/register"><Button size="sm" data-testid="landing-register">Cadastrar</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-4 pt-16 pb-20 md:pt-24 md:pb-28 relative">
          {referrer && (
            <div
              data-testid="landing-referral-banner"
              className="mb-6 inline-flex items-center gap-2 bg-gradient-to-r from-primary/20 to-primary/5 border border-primary/40 rounded-full pl-2 pr-4 py-1.5"
            >
              <span className="text-lg">🎁</span>
              <span className="text-sm">
                Convidado(a) por <strong className="text-primary">{referrer.name?.split(" ")[0]}</strong> — 1º pedido em destaque grátis
              </span>
            </div>
          )}
          <span className="inline-flex items-center gap-2 bg-secondary text-foreground px-3 py-1.5 rounded-full text-xs font-medium border border-border">
            <Sparkles size={12} className="text-primary" /> Primeiro Marketplace Reverso do Brasil
          </span>
          <h1 className="font-heading font-black text-5xl md:text-7xl mt-6 leading-[0.95] tracking-tighter max-w-4xl">
            Você publica. <span className="text-primary">O mercado</span> <span className="underline decoration-primary decoration-[6px] underline-offset-4">responde.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-3xl">
            No Rivendas, você publica o que precisa, quanto está disposto a pagar e como deseja fazer o pagamento.
            <br className="hidden md:block" />
            Empresas, profissionais e pessoas interessadas recebem sua solicitação em tempo real e decidem se querem atender ao seu pedido.
            <br className="hidden md:block" />
            <strong className="text-foreground">Simples, rápido e inteligente.</strong>
          </p>

          {/* 5 primary CTAs on par — Comprar / Vender / Serviços / Permutar / Doar.
              Uses Button asChild so the anchor is the outer interactive element
              (avoids invalid <a><button> nesting that swallowed taps on mobile). */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
            <Button asChild size="lg" className="gap-2 cta-pulse h-14 text-sm md:text-base font-bold">
              <Link to="/register?intent=buyer" data-testid="hero-cta-buyer">
                <ShoppingCart size={18} /> Comprar
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="gap-2 h-14 text-sm md:text-base font-bold">
              <Link to="/register?intent=seller" data-testid="hero-cta-seller">
                <Store size={18} /> Vender
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="gap-2 h-14 text-sm md:text-base font-bold border-primary/40 hover:bg-primary/10">
              <Link to="/register?intent=servico" data-testid="hero-cta-servico">
                <Wrench size={18} /> Serviços
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="gap-2 h-14 text-sm md:text-base font-bold border-primary/40 hover:bg-primary/10">
              <Link to="/register?intent=trocar" data-testid="hero-cta-trocar">
                <Repeat size={18} /> Permutar
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="gap-2 h-14 text-sm md:text-base font-bold border-primary/40 hover:bg-primary/10">
              <Link to="/register?intent=doar" data-testid="hero-cta-doar">
                <Gift size={18} /> Doar
              </Link>
            </Button>
          </div>

          {/* Public stats — social proof */}
          <div data-testid="landing-stats" className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { k: formatCount(stats?.users_total), v: "pessoas na rede", testid: "stat-users" },
              { k: formatCount(stats?.orders_completed), v: "negócios fechados", testid: "stat-completed" },
              { k: formatCount(stats?.trades_open), v: "trocas abertas", testid: "stat-trades" },
              { k: formatCount(stats?.donations_open), v: "doações disponíveis", testid: "stat-donations" },
            ].map((s) => (
              <div key={s.v} data-testid={s.testid} className="bg-card border border-border rounded-xl p-4">
                <p className="font-heading font-black text-2xl md:text-3xl text-primary">{s.k}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.v}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Zap, k: "10min", v: "Timer de aceite" },
              { icon: Sparkles, k: "IA", v: "Sugere preço e categoria" },
              { icon: ShieldCheck, k: "1-5⭐", v: "Reputação transparente" },
              { icon: Users, k: "PF/MEI/CNPJ", v: "Todos os perfis" },
            ].map((f) => {
              const I = f.icon;
              return (
                <div key={f.v} className="bg-card border border-border rounded-xl p-4">
                  <I size={20} className="text-primary mb-3" />
                  <p className="font-heading font-bold text-2xl">{f.k}</p>
                  <p className="text-xs text-muted-foreground">{f.v}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 py-16 md:py-20 border-t border-border">
        <h2 className="font-heading font-black text-3xl md:text-5xl tracking-tight">
          Como funciona
        </h2>
        <p className="mt-4 text-muted-foreground max-w-3xl">
          No Rivendas, quem cria a oportunidade é sempre quem precisa de algo.
          Você pode publicar um pedido de compra, solicitar um serviço, realizar uma troca ou disponibilizar uma doação.
          Empresas, profissionais e pessoas interessadas recebem sua solicitação em tempo real e decidem se desejam atendê-la.
          <br className="hidden md:block" />
          <strong className="text-foreground">Você não procura anúncios. As oportunidades chegam até você.</strong>
        </p>
        <div className="grid md:grid-cols-3 gap-4 mt-10">
          {[
            {
              n: "01",
              t: "Você publica",
              d: "Descreve o que precisa, quanto pode pagar, como quer pagar, cidade e prazo. Anexe fotos e detalhes.",
              i: Clock,
            },
            {
              n: "02",
              t: "O mercado responde",
              d: "Fornecedores compatíveis com sua oportunidade recebem em tempo real e decidem se querem atendê-la.",
              i: Trophy,
            },
            {
              n: "03",
              t: "Negociação em 10 min",
              d: "Chat direto no app, contador de 10 minutos. Se não fechar, a oportunidade é liberada para outro fornecedor.",
              i: Zap,
            },
          ].map((s) => {
            const I = s.i;
            return (
              <div key={s.n} className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono font-bold text-primary text-sm">{s.n}</span>
                  <I size={20} className="text-muted-foreground" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2">{s.t}</h3>
                <p className="text-muted-foreground text-sm">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Showcase: recent closed deals + active people (social proof) */}
      {(showcase.completed_orders.length > 0 || showcase.active_users.length > 0) && (
        <section className="max-w-6xl mx-auto px-4 py-16 md:py-20 border-t border-border" data-testid="landing-showcase">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-primary mb-2">Prova social</p>
              <h2 className="font-heading font-black text-3xl md:text-5xl tracking-tight">
                Negócios reais<br />acontecendo agora
              </h2>
            </div>
          </div>

          {showcase.completed_orders.length > 0 && (
            <div className="mb-12">
              <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-4">Últimos fechados</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="showcase-completed">
                {showcase.completed_orders.map((o) => (
                  <article
                    key={o.order_id}
                    data-testid={`showcase-order-${o.order_id}`}
                    className="bg-card border border-border rounded-xl overflow-hidden group hover:border-primary/60 transition-colors"
                  >
                    <div className="relative aspect-square bg-secondary overflow-hidden">
                      {o.photo ? (
                        <img
                          src={o.photo}
                          alt={o.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform group-hover:scale-[1.04]"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-muted-foreground text-3xl font-heading font-black">
                          {o.order_type === "doacao" ? "🎁" : o.order_type === "permuta" ? "🔄" : "🛒"}
                        </div>
                      )}
                      <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                        Fechado
                      </span>
                      {o.order_type !== "compra" && (
                        <span className="absolute top-2 right-2 bg-black/70 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                          {o.order_type === "doacao" ? "Doação" : "Troca"}
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="font-heading font-bold text-sm leading-tight line-clamp-2">{o.title}</p>
                      <p className="text-[11px] text-muted-foreground mt-1 truncate">
                        {o.buyer_name} · {o.city || "—"}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {showcase.active_users.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-4">Quem está usando</p>
              <div className="flex flex-wrap gap-3" data-testid="showcase-users">
                {showcase.active_users.map((u) => (
                  <div
                    key={u.user_id}
                    data-testid={`showcase-user-${u.user_id}`}
                    className="flex items-center gap-2 bg-card border border-border rounded-full pl-1 pr-3 py-1"
                  >
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-heading font-black text-xs">
                      {u.initials}
                    </span>
                    <div className="text-xs">
                      <p className="font-bold leading-tight">{u.display_name}</p>
                      <p className="text-[10px] text-muted-foreground leading-tight">
                        {u.city || "Brasil"} · {u.role === "seller" ? "Vendedor" : "Comprador"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-4 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row justify-between gap-2">
          <p>© 2026 Rivendas · Você publica. O mercado responde.</p>
          <div className="flex items-center gap-4">
            <Link to="/doacoes" data-testid="footer-donations" className="hover:text-primary flex items-center gap-1">
              <Gift size={14} /> Mural de doações
            </Link>
            <p>Feito no Brasil 🇧🇷</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
